import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, Trash2, PenLine, FileText, ChevronLeft, Link2, Copy, Mail } from "lucide-react";
import { IOSButton, Field, TextArea, StatusBadge, CARD_SHADOW } from "@/components/ios";
import SignatureSheet from "@/components/legal/SignatureSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractDetail({ contractId, token, onChanged, onDeleted, onBack }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [thumbs, setThumbs] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldsDirty, setFieldsDirty] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [fieldType, setFieldType] = useState("signature");
  const [fieldSigner, setFieldSigner] = useState("client");
  const [countersigning, setCountersigning] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const load = useCallback(() => {
    if (!contractId) return;
    setError(null);
    axios
      .get(`${API}/legal/contracts/${contractId}`, auth)
      .then((res) => {
        const d = res.data;
        setDetail(d);
        setTitle(d.title);
        setClientName(d.client_name);
        setClientEmail(d.client_email);
        setBody(d.body || "");
        setFields(d.signature_fields || []);
        setFieldsDirty(false);
        setConfirmDelete(false);
        if (d.kind === "upload" && d.status !== "signed") {
          axios.get(`${API}/legal/contracts/${contractId}/thumbnails`, auth).then((r) => setThumbs(r.data)).catch(() => setThumbs({ pages: [] }));
        } else {
          setThumbs(null);
        }
      })
      .catch(() => setError("Could not load this contract."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, token]);

  useEffect(load, [load]);

  if (error) return <p className="text-[15px] text-[#FF3B30]" data-testid="contract-detail-error">{error}</p>;
  if (!detail)
    return (
      <div className={`bg-white rounded-sm p-10 flex justify-center ${CARD_SHADOW}`}>
        <Loader2 className="w-6 h-6 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
      </div>
    );

  const draft = !["signed", "completed"].includes(detail.status);

  const signLinkUrl = detail.sign_token ? `${window.location.origin}/sign/${detail.sign_token}` : null;

  const createLink = async () => {
    setLinkBusy(true);
    try {
      await axios.post(`${API}/legal/contracts/${contractId}/sign-link`, {}, auth);
      load();
      onChanged();
      toast.success("Signing link created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
    } finally {
      setLinkBusy(false);
    }
  };

  const copyLink = () => {
    const done = () => toast.success("Signing link copied");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(signLinkUrl).then(done).catch(() => window.prompt("Copy signing link", signLinkUrl));
    } else {
      window.prompt("Copy signing link", signLinkUrl);
    }
  };

  const emailLink = async () => {
    setLinkBusy(true);
    try {
      const res = await axios.post(
        `${API}/legal/contracts/${contractId}/send-link`,
        { origin_url: window.location.origin },
        auth
      );
      load();
      onChanged();
      toast.success(`Link emailed to ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not email the link");
    } finally {
      setLinkBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { title, client_name: clientName, client_email: clientEmail };
      if (detail.kind === "template") payload.body = body;
      const res = await axios.patch(`${API}/legal/contracts/${contractId}`, payload, auth);
      setDetail(res.data);
      onChanged();
      toast.success("Contract saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const placeField = (pageNo, e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const w = fieldType === "date" ? 0.22 : 0.28;
    const h = fieldType === "date" ? 0.05 : 0.07;
    const x = Math.min(Math.max((e.clientX - r.left) / r.width - w / 2, 0), 1 - w);
    const y = Math.min(Math.max((e.clientY - r.top) / r.height - h / 2, 0), 1 - h);
    setFields((f) => [...f, { page: pageNo, x, y, w, h, type: fieldType, signer: fieldSigner }]);
    setFieldsDirty(true);
  };

  const saveFields = async () => {
    setSavingFields(true);
    try {
      const res = await axios.put(`${API}/legal/contracts/${contractId}/fields`, { fields }, auth);
      setDetail(res.data);
      setFields(res.data.signature_fields || []);
      setFieldsDirty(false);
      toast.success("Signature fields saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save fields");
    } finally {
      setSavingFields(false);
    }
  };

  const sign = async ({ mode, name, signature_b64 }) => {
    setSigning(true);
    const countersign = detail.status === "signed";
    try {
      const res = await axios.post(
        `${API}/legal/contracts/${contractId}/${countersign ? "countersign" : "sign"}`,
        { mode, name, signature_b64 },
        auth
      );
      setDetail(res.data);
      setSignOpen(false);
      onChanged();
      toast.success(countersign ? "Countersignature applied — contract completed" : "Contract signed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign");
    } finally {
      setSigning(false);
    }
  };

  const download = async () => {
    try {
      const res = await axios.get(`${API}/legal/contracts/${contractId}/pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${detail.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}${detail.status === "signed" ? "-signed" : ""}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/legal/contracts/${contractId}`, auth);
      toast.success("Contract deleted");
      onDeleted(contractId);
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className={`bg-white rounded-sm p-6 md:p-8 ${CARD_SHADOW}`} data-testid="contract-detail">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button onClick={onBack} data-testid="contract-back-button" className="lg:hidden w-9 h-9 rounded-full bg-black/[0.05] flex items-center justify-center active:scale-95 transition-transform" aria-label="Back to list">
              <ChevronLeft className="w-5 h-5 text-[#002B5B]" strokeWidth={2} />
            </button>
          )}
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight truncate" data-testid="contract-detail-title">{detail.title}</h2>
            <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">
              {detail.client_name || "No client"} · created {new Date(detail.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <StatusBadge status={detail.status} />
      </div>

      {detail.status === "signed" && detail.signature && (
        <div className={`mb-6 rounded-sm p-4 ${detail.status === "completed" ? "bg-[#9B703F]/[0.08] border border-[#9B703F]/25" : "bg-[#34C759]/[0.08] border border-[#34C759]/20"}`} data-testid="signed-banner">
          <p className={`text-[14px] font-semibold ${detail.status === "completed" ? "text-[#7A5A33]" : "text-[#248A3D]"}`}>
            {detail.status === "completed" ? "Completed — signed by both parties" : `Signed by ${detail.signature.name}`}
          </p>
          <p className={`text-[13px] mt-0.5 ${detail.status === "completed" ? "text-[#7A5A33]/80" : "text-[#248A3D]/80"}`}>
            {detail.signature.mode} signature · {new Date(detail.signature.at).toLocaleString()}
            {detail.countersignature && ` · countersigned by ${detail.countersignature.name} · ${new Date(detail.countersignature.at).toLocaleString()}`}
          </p>
        </div>
      )}

      {draft && (
        <div className="mb-6 rounded-sm bg-[#F2F2F7] p-4" data-testid="sign-link-section">
          <p className="text-[13px] font-medium text-[#3C3C43]/70 mb-3 flex items-center gap-1.5">
            <Link2 className="w-4 h-4" strokeWidth={1.5} /> Client signing link
          </p>
          {signLinkUrl ? (
            <>
              <input
                readOnly
                value={signLinkUrl}
                data-testid="sign-link-url"
                onFocus={(e) => e.target.select()}
                className="w-full min-h-[44px] rounded-sm bg-white border border-black/[0.05] px-4 text-[13px] font-mono text-[#3C3C43]/80 mb-3 focus:outline-none"
              />
              <div className="flex flex-wrap gap-2">
                <IOSButton variant="plain" data-testid="copy-sign-link-button" onClick={copyLink} className="min-h-[40px] px-4 text-[13px]">
                  <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy link
                </IOSButton>
                <IOSButton
                  variant="plain"
                  data-testid="email-sign-link-button"
                  onClick={emailLink}
                  disabled={linkBusy || !detail.client_email}
                  className="min-h-[40px] px-4 text-[13px]"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5} /> Email to client
                </IOSButton>
              </div>
              <p className="text-[12px] text-[#3C3C43]/50 mt-2.5">
                {detail.client_email ? "" : "Add a client email above to send this link. "}Link expires 14 days after creation.
              </p>
            </>
          ) : (
            <>
              <p className="text-[13px] text-[#3C3C43]/60 mb-3">
                Create a secure link so your client can review and sign — no Doclyx account needed.
              </p>
              <IOSButton data-testid="create-sign-link-button" onClick={createLink} disabled={linkBusy} className="min-h-[40px] px-4 text-[13px]">
                {linkBusy ? "Creating…" : "Create signing link"}
              </IOSButton>
            </>
          )}
        </div>
      )}

      {draft && (
        <div className="space-y-4 mb-6">
          <Field label="Document title" testid="contract-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Client name" testid="contract-client-name-input" value={clientName} onChange={(e) => setClientName(e.target.value)} maxLength={120} />
            <Field label="Client email" testid="contract-client-email-input" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} maxLength={120} />
          </div>
          {detail.kind === "template" && (
            <TextArea label="Contract text" testid="contract-body-input" rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-[13px]" />
          )}
          <IOSButton variant="plain" data-testid="contract-save-button" onClick={save} disabled={saving || !title.trim()}>
            {saving ? "Saving…" : "Save changes"}
          </IOSButton>
        </div>
      )}

      {detail.kind === "upload" && draft && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-[13px] font-medium text-[#3C3C43]/70" data-testid="place-fields-hint">
              Tap a page to place the selected field — tap a field to remove it
            </p>
            {fieldsDirty && (
              <IOSButton variant="primary" data-testid="save-fields-button" onClick={saveFields} disabled={savingFields} className="min-h-[36px] px-4 text-[13px]">
                {savingFields ? "Saving…" : `Save ${fields.length} field${fields.length === 1 ? "" : "s"}`}
              </IOSButton>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="field-toolbar">
            <div className="flex gap-1 p-0.5 bg-black/[0.04] rounded-full">
              {[["signature", "Signature"], ["date", "Date"]].map(([id, label]) => (
                <button key={id} type="button" data-testid={`field-type-${id}`} onClick={() => setFieldType(id)}
                  className={`px-3 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-150 ${fieldType === id ? "bg-[#111110] text-white" : "text-[#3C3C43]/60 hover:text-[#111110]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-1 p-0.5 bg-black/[0.04] rounded-full">
              {[["client", "Client signs"], ["admin", "You countersign"]].map(([id, label]) => (
                <button key={id} type="button" data-testid={`field-signer-${id}`} onClick={() => setFieldSigner(id)}
                  className={`px-3 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-150 ${fieldSigner === id ? "bg-[#111110] text-white" : "text-[#3C3C43]/60 hover:text-[#111110]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-4 max-h-[480px] overflow-y-auto rounded-sm bg-[#FDFDFB] border border-hairline p-4">
            {(thumbs?.pages || []).map((p) => (
              <div key={p.page} className="relative bg-white rounded-sm overflow-hidden shadow-sm cursor-copy" onClick={(e) => placeField(p.page, e)} data-testid={`contract-thumb-page-${p.page}`}>
                <img src={`data:image/png;base64,${p.image}`} alt={`Page ${p.page}`} className="w-full select-none" draggable={false} />
                {fields.map((f, i) => {
                  if (f.page !== p.page) return null;
                  const isDate = f.type === "date";
                  const isAdmin = f.signer === "admin";
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`signature-field-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFields((fl) => fl.filter((_, j) => j !== i));
                        setFieldsDirty(true);
                      }}
                      className={`absolute border-2 border-dashed bg-white/95 rounded-sm flex items-center justify-center active:scale-95 transition-transform shadow-sm ${
                        isAdmin ? "border-[#9B703F]" : isDate ? "border-[#9B703F]" : "border-[#002B5B]"
                      }`}
                      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}
                      title="Tap to remove"
                    >
                      <span className={`text-[10px] font-semibold flex items-center gap-1 ${isAdmin || isDate ? "text-[#9B703F]" : "text-[#002B5B]"}`}>
                        {isDate ? <FileText className="w-3 h-3" strokeWidth={2} /> : <PenLine className="w-3 h-3" strokeWidth={2} />}
                        {isDate ? "Date" : "Sign here"}{isAdmin ? " · you" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            {thumbs && thumbs.pages.length === 0 && <p className="text-[14px] text-[#3C3C43]/50 py-6 text-center">No preview available</p>}
            {!thumbs && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        {draft && (
          <IOSButton data-testid="sign-contract-button" onClick={() => setSignOpen(true)}>
            <PenLine className="w-4 h-4" strokeWidth={1.5} /> Sign document
          </IOSButton>
        )}
        {detail.status === "signed" && !detail.countersignature && (
          <IOSButton data-testid="countersign-button" onClick={() => setSignOpen(true)}>
            <PenLine className="w-4 h-4" strokeWidth={1.5} /> Countersign
          </IOSButton>
        )}
        <IOSButton variant="plain" data-testid="download-contract-button" onClick={download}>
          <Download className="w-4 h-4" strokeWidth={1.5} /> {["signed", "completed"].includes(detail.status) ? "Download signed PDF" : "Preview PDF"}
        </IOSButton>
        {confirmDelete ? (
          <IOSButton variant="destructive" data-testid="delete-confirm-button" onClick={remove}>
            Confirm delete
          </IOSButton>
        ) : (
          <IOSButton variant="destructive" data-testid="delete-contract-button" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
          </IOSButton>
        )}
      </div>

      {(detail.audit || []).length > 0 && (
        <div data-testid="audit-trail">
          <p className="text-[13px] font-medium text-[#3C3C43]/70 mb-2 flex items-center gap-1.5">
            <FileText className="w-4 h-4" strokeWidth={1.5} /> Audit trail
          </p>
          <div className="rounded-sm bg-[#F2F2F7] divide-y divide-black/[0.04]">
            {detail.audit.map((ev, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between text-[13px]" data-testid={`audit-event-${i}`}>
                <span className="capitalize text-[#0A0A0A]">{ev.event.replaceAll("_", " ")}</span>
                <span className="text-[#3C3C43]/50">{new Date(ev.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <SignatureSheet open={signOpen} onClose={() => setSignOpen(false)} onSubmit={sign} busy={signing} />
    </div>
  );
}
